import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Users,
  ArrowLeft,
  Loader2,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileText,
  CreditCard,
  Search,
  Landmark,
  Receipt,
} from "lucide-react";
import {
  accountingApi,
  bankAccountsApi,
  mattersApi,
  trustApi,
  invoicesApi,
  paymentsApi,
} from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import { useUserDataContext } from "../../context/UserDataContext";

interface ClientSummary {
  clientId: string;
  clientName: string;
  trustBalanceCents: number;
  totalBilledCents: number;
  totalPaidCents: number;
  arBalanceCents: number;
}

interface TrustMovement {
  _id: string;
  date: string;
  description: string;
  type: "receipt" | "disbursement";
  amountCents: number;
  status: string;
}

interface BillingActivity {
  _id: string;
  date: string;
  description: string;
  type: "invoice" | "payment";
  amountCents: number;
  paidCents?: number;
  status: string;
}

interface ClientDetail {
  trust: {
    balanceCents: number;
    availableBalanceCents: number;
    movements: TrustMovement[];
  };
  billing: {
    totalBilledCents: number;
    totalPaidCents: number;
    balanceOwingCents: number;
    activity: BillingActivity[];
  };
}

function formatCents(cents: number): string {
  const isNegative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return isNegative ? `-${formatted}` : formatted;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const METHODS = [
  { value: "eft", label: "EFT" },
  { value: "wire", label: "Wire Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
];

const PAYMENT_SOURCES = [
  { value: "eft", label: "EFT" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "trust_transfer", label: "Trust Transfer" },
];

const ClientBalancesTab: React.FC = () => {
  const { canManageBilling } = useUserDataContext();
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Detail view
  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null);
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modal state
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  // null = unknown (still loading or lookup failed); false = firm has no open bank accounts
  const [hasBankAccounts, setHasBankAccounts] = useState<boolean | null>(null);

  // Receipt form state
  const [receiptBankAccountId, setReceiptBankAccountId] = useState<string | null>(null);
  const [receiptMatterId, setReceiptMatterId] = useState<string | null>(null);
  const [receiptAmount, setReceiptAmount] = useState("");
  const [receiptMethod, setReceiptMethod] = useState<string | null>(null);
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);
  const [receiptError, setReceiptError] = useState("");

  // Payment form state
  const [paymentInvoiceId, setPaymentInvoiceId] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentSource, setPaymentSource] = useState<string | null>(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // Lookup data for modals
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [matters, setMatters] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Load all client summaries
  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountingApi.clientBalances({ search: search || undefined });
      setClients(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load client balances:", err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (!selectedClient) {
      loadSummary();
    }
  }, [loadSummary, selectedClient]);

  // Load client detail
  const loadDetail = useCallback(async (clientId: string) => {
    setDetailLoading(true);
    try {
      const res = await accountingApi.clientBalances({ clientId });
      setDetail(res.data);
    } catch (err) {
      console.error("Failed to load client detail:", err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleSelectClient = (client: ClientSummary) => {
    setSelectedClient(client);
    loadDetail(client.clientId);
  };

  const handleBack = () => {
    setSelectedClient(null);
    setDetail(null);
  };

  // Record Receipt deposits into a bank account, so hide it when the firm has none
  useEffect(() => {
    if (!selectedClient || !canManageBilling || hasBankAccounts !== null) return;
    let cancelled = false;
    bankAccountsApi
      .list({ status: "open" })
      .then((res) => {
        const list = res.data?.data || res.data || [];
        if (!cancelled) setHasBankAccounts(Array.isArray(list) && list.length > 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedClient, canManageBilling, hasBankAccounts]);

  // ── Load lookup data for modals ──
  const loadReceiptLookups = useCallback(async (clientId: string) => {
    setLookupLoading(true);
    try {
      const [bankRes, matterRes] = await Promise.all([
        bankAccountsApi.list({ status: "open" }),
        mattersApi.list({ clientId }),
      ]);
      setBankAccounts(bankRes.data?.data || bankRes.data || []);
      setMatters(matterRes.data?.data || matterRes.data || []);
    } catch (err) {
      console.error("Failed to load receipt lookups:", err);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  const loadPaymentLookups = useCallback(async (clientId: string) => {
    setLookupLoading(true);
    try {
      const [issuedRes, partialRes, matterRes] = await Promise.all([
        invoicesApi.list({ clientId, status: "issued" }),
        invoicesApi.list({ clientId, status: "partial" }),
        mattersApi.list({ clientId }),
      ]);
      const issued = issuedRes.data?.data || issuedRes.data || [];
      const partial = partialRes.data?.data || partialRes.data || [];
      setInvoices([...issued, ...partial]);
      setMatters(matterRes.data?.data || matterRes.data || []);
    } catch (err) {
      console.error("Failed to load payment lookups:", err);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  // ── Open receipt modal ──
  const openReceiptModal = () => {
    if (!selectedClient) return;
    setReceiptBankAccountId(null);
    setReceiptMatterId(null);
    setReceiptAmount("");
    setReceiptMethod(null);
    setReceiptReference("");
    setReceiptError("");
    setReceiptSubmitting(false);
    loadReceiptLookups(selectedClient.clientId);
    setReceiptModalOpen(true);
  };

  // ── Open payment modal ──
  const openPaymentModal = () => {
    if (!selectedClient) return;
    setPaymentInvoiceId(null);
    setPaymentMatterId(null);
    setPaymentAmount("");
    setPaymentSource(null);
    setPaymentError("");
    setPaymentSubmitting(false);
    loadPaymentLookups(selectedClient.clientId);
    setPaymentModalOpen(true);
  };

  // Helper: is the selected receipt account a trust account?
  const selectedReceiptAccount = bankAccounts.find((a: any) => a._id === receiptBankAccountId);
  const isReceiptTrustAccount = selectedReceiptAccount?.kind === "trust";

  // ── Submit receipt ──
  const handleSubmitReceipt = async () => {
    if (!selectedClient) return;
    if (!receiptBankAccountId || !receiptAmount || !receiptMethod) {
      setReceiptError("Please select a bank account, enter an amount, and choose a method.");
      return;
    }
    if (isReceiptTrustAccount && !receiptMatterId) {
      setReceiptError("Matter is required for trust account deposits.");
      return;
    }
    const cents = Math.round(parseFloat(receiptAmount) * 100);
    if (isNaN(cents) || cents <= 0) {
      setReceiptError("Please enter a valid amount greater than zero.");
      return;
    }
    setReceiptSubmitting(true);
    setReceiptError("");
    try {
      if (isReceiptTrustAccount) {
        // Trust account → create trust receipt
        await trustApi.createReceipt({
          bankAccountId: receiptBankAccountId,
          clientId: selectedClient.clientId,
          matterId: receiptMatterId!,
          amountCents: cents,
          method: receiptMethod,
          payer: {
            kind: "client",
            clientId: selectedClient.clientId,
            name: selectedClient.clientName,
          },
          reference: receiptReference || undefined,
        });
      } else {
        // Non-trust account → record as payment on account
        await paymentsApi.create({
          clientId: selectedClient.clientId,
          matterId: receiptMatterId || undefined,
          amountCents: cents,
          source: receiptMethod,
          methodDetails: {
            receivedFrom: selectedClient.clientName,
            notes: receiptReference || undefined,
          },
        });
      }
      setReceiptModalOpen(false);
      loadDetail(selectedClient.clientId);
    } catch (err: any) {
      setReceiptError(err?.response?.data?.message || err?.response?.data?.error || "Failed to create receipt.");
    } finally {
      setReceiptSubmitting(false);
    }
  };

  // Payment matter (for payment on account without invoice)
  const [paymentMatterId, setPaymentMatterId] = useState<string | null>(null);

  // ── Submit payment ──
  const handleSubmitPayment = async () => {
    if (!selectedClient) return;
    if (!paymentAmount || !paymentSource) {
      setPaymentError("Please enter an amount and select a payment source.");
      return;
    }
    const cents = Math.round(parseFloat(paymentAmount) * 100);
    if (isNaN(cents) || cents <= 0) {
      setPaymentError("Please enter a valid amount greater than zero.");
      return;
    }
    const invoice = paymentInvoiceId ? invoices.find((inv: any) => inv._id === paymentInvoiceId) : null;
    setPaymentSubmitting(true);
    setPaymentError("");
    try {
      await paymentsApi.create({
        invoiceId: paymentInvoiceId || undefined,
        clientId: selectedClient.clientId,
        matterId: invoice?.matterId || paymentMatterId || undefined,
        amountCents: cents,
        source: paymentSource,
      });
      setPaymentModalOpen(false);
      loadDetail(selectedClient.clientId);
    } catch (err: any) {
      setPaymentError(err?.response?.data?.message || err?.response?.data?.error || "Failed to record payment.");
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // When an invoice is selected, default the amount to balance due
  const handleInvoiceSelect = (invoiceId: string | null) => {
    setPaymentInvoiceId(invoiceId);
    if (invoiceId) {
      const inv = invoices.find((i: any) => i._id === invoiceId);
      if (inv) {
        const balanceDue = inv.balanceDueCents ?? (inv.totalCents - (inv.paidCents || 0));
        if (balanceDue > 0) {
          setPaymentAmount((balanceDue / 100).toFixed(2));
        }
      }
    }
  };

  // ── Detail View ──
  if (selectedClient) {
    return (
      <div>
        {/* Back button + client name + action buttons */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBack}
            className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-800">
              {selectedClient.clientName}
            </h2>
            <p className="text-xs text-slate-500">Client Account Summary</p>
          </div>
          {canManageBilling && (
          <div className="flex items-center gap-2">
            {hasBankAccounts !== false && (
              <button
                onClick={openReceiptModal}
                title="Money received from the client into a bank account, usually a trust retainer held for future bills"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border border-emerald-200 text-brand bg-emerald-50 hover:bg-emerald-100 transition-colors"
              >
                <ArrowDownToLine className="h-3.5 w-3.5" />
                Record Receipt
              </button>
            )}
            <button
              onClick={openPaymentModal}
              title="Money the client paid toward one of their invoices"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <CreditCard className="h-3.5 w-3.5" />
              Record Payment
            </button>
          </div>
          )}
        </div>
        {canManageBilling && (
          <p className="-mt-4 mb-6 text-xs text-slate-500">
            {hasBankAccounts !== false && (
              <>
                <span className="font-semibold text-slate-600">Receipt</span>: money into a bank account, usually trust funds held for the client.{" "}
              </>
            )}
            <span className="font-semibold text-slate-600">Payment</span>: money applied to one of this client's invoices.
          </p>
        )}

        {detailLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : detail ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                {
                  label: "Trust Balance",
                  value: formatCents(detail.trust.balanceCents),
                  Icon: Landmark,
                  color: "text-emerald-700",
                  bg: "bg-emerald-50",
                },
                {
                  label: "Available (Trust)",
                  value: formatCents(detail.trust.availableBalanceCents),
                  Icon: DollarSign,
                  color: "text-blue-700",
                  bg: "bg-blue-50",
                },
                {
                  label: "Total Billed",
                  value: formatCents(detail.billing.totalBilledCents),
                  Icon: FileText,
                  color: "text-slate-700",
                  bg: "bg-slate-100",
                },
                {
                  label: "Balance Owing",
                  value: formatCents(detail.billing.balanceOwingCents),
                  Icon: Receipt,
                  color:
                    detail.billing.balanceOwingCents > 0
                      ? "text-red-700"
                      : "text-emerald-700",
                  bg:
                    detail.billing.balanceOwingCents > 0
                      ? "bg-red-50"
                      : "bg-emerald-50",
                },
              ].map((card, i) => (
                <motion.div
                  key={card.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${card.bg}`}>
                      <card.Icon className={`h-4 w-4 ${card.color}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">
                        {card.label}
                      </p>
                      <p className={`text-lg font-bold ${card.color}`}>
                        {card.value}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Two-panel layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trust Account Panel */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-brand rounded-t-xl">
                  <Landmark className="h-4 w-4 text-white" />
                  <h3 className="text-sm font-bold text-white">
                    Trust Account
                  </h3>
                </div>

                {detail.trust.movements.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">
                    No trust activity
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left">
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500">
                            Date
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500">
                            Description
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500 text-right">
                            Receipts (In)
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500 text-right">
                            Disbursements (Out)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {detail.trust.movements.map((m, i) => (
                          <motion.tr
                            key={m._id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.02 }}
                            className="hover:bg-slate-50"
                          >
                            <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                              {formatDate(m.date)}
                            </td>
                            <td className="px-4 py-2.5 text-slate-800 font-medium truncate max-w-[200px]">
                              {m.description}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono">
                              {m.type === "receipt" ? (
                                <span className="text-emerald-700 inline-flex items-center gap-1">
                                  <ArrowDownToLine className="h-3 w-3" />
                                  {formatCents(m.amountCents)}
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono">
                              {m.type === "disbursement" ? (
                                <span className="text-red-600 inline-flex items-center gap-1">
                                  <ArrowUpFromLine className="h-3 w-3" />
                                  {formatCents(m.amountCents)}
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50 font-semibold">
                          <td
                            colSpan={2}
                            className="px-4 py-2.5 text-xs text-slate-600"
                          >
                            Trust Balance
                          </td>
                          <td
                            colSpan={2}
                            className="px-4 py-2.5 text-right font-mono text-emerald-700"
                          >
                            {formatCents(detail.trust.balanceCents)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* Accounts Receivable Panel */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-brand rounded-t-xl">
                  <Receipt className="h-4 w-4 text-white" />
                  <h3 className="text-sm font-bold text-white">
                    Accounts Receivable
                  </h3>
                </div>

                {detail.billing.activity.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">
                    No billing activity
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left">
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500">
                            Date
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500">
                            Description
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500 text-right">
                            Billed
                          </th>
                          <th className="px-4 py-2 text-xs font-semibold text-slate-500 text-right">
                            Paid
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {detail.billing.activity.map((a, i) => (
                          <motion.tr
                            key={a._id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.02 }}
                            className="hover:bg-slate-50"
                          >
                            <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                              {formatDate(a.date)}
                            </td>
                            <td className="px-4 py-2.5 text-slate-800 font-medium truncate max-w-[200px]">
                              {a.description}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono">
                              {a.type === "invoice" ? (
                                <span className="text-slate-700 inline-flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  {formatCents(a.amountCents)}
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono">
                              {a.type === "payment" ? (
                                <span className="text-emerald-700 inline-flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  {formatCents(a.amountCents)}
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50 font-semibold">
                          <td
                            colSpan={2}
                            className="px-4 py-2.5 text-xs text-slate-600"
                          >
                            Balance Owing
                          </td>
                          <td
                            colSpan={2}
                            className={`px-4 py-2.5 text-right font-mono ${
                              detail.billing.balanceOwingCents > 0
                                ? "text-red-600"
                                : "text-emerald-700"
                            }`}
                          >
                            {formatCents(detail.billing.balanceOwingCents)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}

        {/* ── Record Receipt Modal ── */}
        <PremiumModal
          open={receiptModalOpen}
          onClose={() => setReceiptModalOpen(false)}
          title="Record Receipt"
          subtitle={selectedClient?.clientName}
          icon={ArrowDownToLine}
          size="md"
          footer={
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setReceiptModalOpen(false)}
                className="px-4 py-2.5 text-sm font-medium text-slate-600 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitReceipt}
                disabled={receiptSubmitting}
                className="px-5 py-2.5 text-sm font-bold text-white rounded-xl bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50"
              >
                {receiptSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  "Record Receipt"
                )}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Use this for money received that is not paying a specific invoice, such as a retainer deposited to trust. To pay an invoice, use Record Payment.
            </p>
            {receiptError && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                {receiptError}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Deposit To <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={bankAccounts.map((a: any) => {
                  const kindLabel = a.kind === "trust" ? "Trust" : a.kind === "operating" ? "Operating" : a.kind === "savings" ? "Savings" : a.kind?.replace("_", " ");
                  return {
                    value: a._id,
                    label: `${a.name}${a.accountLast4 ? ` (...${a.accountLast4})` : ""}`,
                    description: kindLabel,
                  };
                })}
                value={receiptBankAccountId}
                onChange={setReceiptBankAccountId}
                placeholder="Select bank account..."
                loading={lookupLoading}
                icon={Landmark}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Matter {isReceiptTrustAccount && <span className="text-red-500">*</span>}
              </label>
              <SearchableSelect
                options={matters.map((m: any) => ({
                  value: m._id,
                  label: m.title || m.name,
                  description: m.matterNumber || undefined,
                  icon: FileText,
                }))}
                value={receiptMatterId}
                onChange={setReceiptMatterId}
                placeholder={isReceiptTrustAccount ? "Select matter (required)..." : "Select matter (optional)..."}
                loading={lookupLoading}
                icon={FileText}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Amount (CAD) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={receiptAmount}
                  onChange={(e) => setReceiptAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Method <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={METHODS}
                value={receiptMethod}
                onChange={setReceiptMethod}
                placeholder="Select method..."
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Reference / Notes
              </label>
              <input
                type="text"
                value={receiptReference}
                onChange={(e) => setReceiptReference(e.target.value)}
                placeholder="Cheque number, reference ID, or notes..."
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>
        </PremiumModal>

        {/* ── Record Payment Modal ── */}
        <PremiumModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          title="Record Payment"
          subtitle={selectedClient?.clientName}
          icon={CreditCard}
          size="md"
          footer={
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setPaymentModalOpen(false)}
                className="px-4 py-2.5 text-sm font-medium text-slate-600 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitPayment}
                disabled={paymentSubmitting}
                className="px-5 py-2.5 text-sm font-bold text-white rounded-xl bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50"
              >
                {paymentSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  "Record Payment"
                )}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Use this when the client pays an invoice. Pick the invoice to apply it to, or leave it blank to record a payment on account.
            </p>
            {paymentError && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                {paymentError}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Apply to Invoice
              </label>
              <SearchableSelect
                options={invoices.map((inv: any) => ({
                  value: inv._id,
                  label: `${inv.invoiceNumber || inv._id.slice(-6).toUpperCase()} - ${formatCents(inv.totalCents || 0)}`,
                  description: inv.balanceDueCents != null
                    ? `Balance due: ${formatCents(inv.balanceDueCents)}`
                    : undefined,
                  icon: FileText,
                }))}
                value={paymentInvoiceId}
                onChange={handleInvoiceSelect}
                placeholder={invoices.length === 0 ? "No outstanding invoices" : "Select invoice (optional)..."}
                loading={lookupLoading}
                icon={FileText}
              />
              {!paymentInvoiceId && (
                <p className="text-xs text-slate-400 mt-1">
                  Leave blank to record as payment on account
                </p>
              )}
            </div>

            {!paymentInvoiceId && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Matter
                </label>
                <SearchableSelect
                  options={matters.map((m: any) => ({
                    value: m._id,
                    label: m.title || m.name,
                    description: m.matterNumber || undefined,
                    icon: FileText,
                  }))}
                  value={paymentMatterId}
                  onChange={setPaymentMatterId}
                  placeholder="Select matter (optional)..."
                  loading={lookupLoading}
                  icon={FileText}
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Amount (CAD) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Source <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={PAYMENT_SOURCES}
                value={paymentSource}
                onChange={setPaymentSource}
                placeholder="Select payment source..."
              />
            </div>
          </div>
        </PremiumModal>
      </div>
    );
  }

  // ── Summary View (all clients) ──
  return (
    <div>
      {/* Search bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients by name..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
          />
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-slate-500 mb-3">
        {clients.length} client{clients.length !== 1 ? "s" : ""}
      </p>

      {/* Client Summary Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-600">
            {search ? "No matching clients" : "No clients yet"}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {search
              ? "Try a different search term"
              : "Add clients to see their trust and billing balances here"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand text-white text-left">
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Trust Balance
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Total Billed
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Total Paid
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">
                    A/R Owing
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clients.map((client, i) => (
                  <motion.tr
                    key={client.clientId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.02 }}
                    onClick={() => handleSelectClient(client)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {client.clientName}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {client.trustBalanceCents > 0 ? (
                        <span className="text-emerald-700">
                          {formatCents(client.trustBalanceCents)}
                        </span>
                      ) : client.trustBalanceCents < 0 ? (
                        <span className="text-red-600">
                          {formatCents(client.trustBalanceCents)}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">
                      {client.totalBilledCents > 0
                        ? formatCents(client.totalBilledCents)
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">
                      {client.totalPaidCents > 0
                        ? formatCents(client.totalPaidCents)
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {client.arBalanceCents > 0 ? (
                        <span className="text-red-600 font-semibold">
                          {formatCents(client.arBalanceCents)}
                        </span>
                      ) : client.arBalanceCents === 0 &&
                        client.totalBilledCents > 0 ? (
                        <span className="text-emerald-700 font-semibold">
                          Paid
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientBalancesTab;
